import {date, formatDatePeriod, rangeForPeriod, rangeToDateArray} from '@eitje/utils'
import {
	DatePickerProvider,
	DatePickerControl,
	DatePickerDayView,
	DatePickerMonthView,
	DatePickerPickerRoot,
	DatePickerQuarterView,
	DatePickerRoot,
	DatePickerTrigger,
	DatePickerYearView,
} from '~/components/date_based_pickers/components'
import {EitjeButton} from '~/components/eitje_button'
import {Text} from '~/components/text'
import {useT} from '@eitje/i18n'

/**
 * @typedef {Object} DatePeriodPickerProps
 * @property {string | import('moment').Moment | null} [value]
 * A single date that belongs to the period
 * @property {'week' | 'month' | 'quarter'} periodType Period represented by a selected date.
 * @property {(value: import('moment').Moment[]) => void} onChange Called with every date in the selected period.
 * @property {string | import('moment').Moment} [defaultPickerValue] Initial date focused when the picker opens.
 * @property {string} [placeholder] Placeholder shown by the default trigger when no period is selected.
 * @property {(datePicker: import('~/components/date_based_pickers/helpers').DatePickerContextValues) => import('react').ReactNode} [renderTrigger]
 * Renders the trigger with the adapted date-picker context. The context value is the start of the selected period.
 * @property {(datePicker: import('~/components/date_based_pickers/helpers').SingleDatePickerContextValues) => import('react').ReactNode} [renderExtraFooter]
 * Renders additional content below the picker views with the adapted date-picker context.
 * @property {string | import('moment').Moment} [minDate] Earliest selectable date, inclusively.
 * @property {string | import('moment').Moment} [maxDate] Latest selectable date, inclusively.
 * @property {boolean} [disabled] Prevents interaction with the picker.
 * @property {boolean} [readOnly] Prevents value changes while retaining the selected period.
 * @property {(open: boolean) => void} [onOpenChange] Reports whether the picker is open.
 * @property {boolean} [showCurrPeriodFooter] Shows the `Current {{period}}` and `Previous {{period}}` footer options.
 * @property {(date: import('moment').Moment) => boolean} [disabledDateFn] Predicate for whether a date should be disabled.
 */

/** @param {DatePeriodPickerProps} props */
export const DatePeriodPicker = ({
	value,
	defaultPickerValue,
	placeholder,
	periodType,
	minDate,
	maxDate,
	disabled,
	readOnly,
	showCurrPeriodFooter,
	renderExtraFooter,
	renderTrigger,
	onOpenChange,
	onChange,
	disabledDateFn,
}) => {
	const {t} = useT()

	const handleChange = dates => {
		const selectedDate = dates[dates.length - 1]
		if (!selectedDate) return

		const nextPeriod = rangeForPeriod({date: selectedDate, period: periodType})
		onChange(rangeToDateArray(nextPeriod))
	}

	let triggerElement = (
		<DatePickerProvider>
			{ctx => (
				<DatePickerTrigger>
					<EitjeButton dropdown>
						<Text>{ctx.value ? formatDatePeriod(ctx.value, periodType) : placeholder || '...'}</Text>
					</EitjeButton>
				</DatePickerTrigger>
			)}
		</DatePickerProvider>
	)
	if (renderTrigger)
		triggerElement = <DatePickerProvider>{ctx => <DatePickerTrigger>{renderTrigger(ctx)}</DatePickerTrigger>}</DatePickerProvider>

	const selectedPeriod = value ? rangeForPeriod({date: value, period: periodType}) : undefined
	const minimumDate = minDate ? date(minDate) : undefined
	const maximumDate = maxDate ? date(maxDate) : undefined
	const defaultFocusedValueOverride = defaultPickerValue ? date(defaultPickerValue) : selectedPeriod?.start
	const valueOverride = selectedPeriod ? [selectedPeriod.start] : []
	const pickerView = periodType === 'week' ? 'day' : 'month'

	return (
		<DatePickerRoot
			key={periodType}
			value={valueOverride}
			defaultPickerView={pickerView}
			minPickerView={pickerView}
			defaultFocusedValue={defaultFocusedValueOverride}
			minDate={minimumDate}
			maxDate={maximumDate}
			disabled={disabled}
			readOnly={readOnly}
			onOpenChange={onOpenChange}
			onChange={handleChange}
			disabledDateFn={disabledDateFn}
		>
			<DatePickerControl>{triggerElement}</DatePickerControl>
			<DatePickerPickerRoot
				renderExtraFooter={
					(showCurrPeriodFooter || renderExtraFooter) &&
					(ctx => (
						<>
							{showCurrPeriodFooter && (
								<EitjeButton
									height="small"
									onClick={() => {
										ctx.setValue(date().startOf(periodType))
										ctx.setOpen(false)
									}}
								>
									{`${t('common.current')} ${t(`common.${periodType}`)}`}
								</EitjeButton>
							)}
							{renderExtraFooter?.(ctx)}
						</>
					))
				}
			>
				{periodType === 'week' && <DatePickerDayView />}
				{(periodType === 'week' || periodType === 'month') && <DatePickerMonthView />}
				{periodType === 'quarter' && <DatePickerQuarterView />}
				<DatePickerYearView />
			</DatePickerPickerRoot>
		</DatePickerRoot>
	)
}
